import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, defaultIfEmpty, EMPTY, map, Observable, of, Subscription, tap } from 'rxjs';
import { UserAuth } from '../models/user.auth.model';
import { UtilService } from './util.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

export interface AuthenticationTokens {
  token: string;
  refreshToken: string;
}

export type WebApplication = 'ALERTS' | 'ADMINISTRATION' | 'DASHBOARD';

@Injectable({
  providedIn: 'root',
})
export class SeguridadService {
  private _obsUser: BehaviorSubject<UserAuth> = new BehaviorSubject<UserAuth>(
    {} as UserAuth
  );

  public user$ = this._obsUser.asObservable();

  private urlServiceAdmin = environment.API_SERVICES_ADMIN;
  private urlBaseSecurity = environment.API_SERVICES_SECURITY;
  urlAuth = `${this.urlServiceAdmin}/auth/login`;
  urlSecurity = `${this.urlBaseSecurity}/api/admin/security`;
  url = `${this.urlServiceAdmin}/api`;

  subSessionRefresh!: Subscription;

  constructor(
    public utilService: UtilService,
    private jwtHelper: JwtHelperService,
    private http: HttpClient
  ) {}

  refreshSessionData(): void {
    if (!this.getToken()) {
      return;
    }
    const token = this.getToken();
    if (token && token !== '') {
      this.setToken(environment.VAR_TOKEN, token);
    }
  }

  refreshSessionToken(): Observable<AuthenticationTokens> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return EMPTY;
    }
    return this.http
      .post<AuthenticationTokens>(`${this.urlServiceAdmin}/auth/refresh`, {
        refreshToken,
        rotate: true,
      })
      .pipe(tap((tokens) => this.saveAuthentication(tokens)));
  }

  ensureAuthenticated(): Observable<boolean> {
    if (this.isAuthenticated()) {
      this.refreshSessionData();
      return of(true);
    }
    if (!this.getRefreshToken()) {
      return of(false);
    }
    return this.refreshSessionToken().pipe(
      map(() => this.isAuthenticated()),
      defaultIfEmpty(false),
      catchError(() => {
        this.clearLocalStorage();
        return of(false);
      })
    );
  }

  switchApplication(
    application: WebApplication
  ): Observable<AuthenticationTokens> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return EMPTY;
    }
    return this.http
      .post<AuthenticationTokens>(
        `${this.urlServiceAdmin}/auth/switch-application`,
        { refreshToken, application }
      )
      .pipe(tap((tokens) => this.saveAuthentication(tokens)));
  }

  getManagerApplication(): WebApplication | null {
    const user = this.getStoredUser();
    if (user?.applications?.includes('ADMINISTRATION')) {
      return 'ADMINISTRATION';
    }
    if (user?.applications?.includes('DASHBOARD')) {
      return 'DASHBOARD';
    }
    return null;
  }

  canAccessApplication(application: WebApplication): boolean {
    return this.getStoredUser()?.applications?.includes(application) ?? false;
  }

  isReadOnly(): boolean {
    const roles = this.getStoredUser()?.roles ?? [];
    const hasConsultation = roles.includes(1062);
    const hasWriteRole = roles.some((role) =>
      [1050, 1060, 1061].includes(role)
    );
    return hasConsultation && !hasWriteRole;
  }

  setToken(tokenName: string, token?: string) {
    if (token) {
      this.setUser(token);
    }
    return this.utilService.setSessionStorage(tokenName, token);
  }

  getToken() {
    return this.utilService.getSessionStorage(environment.VAR_TOKEN);
  }

  getRefreshToken(): string | null {
    return this.utilService.getSessionStorage(environment.VAR_REFRESH_TOKEN);
  }

  saveAuthentication(tokens: AuthenticationTokens): void {
    this.setToken(environment.VAR_TOKEN, tokens.token);
    this.utilService.setSessionStorage(
      environment.VAR_REFRESH_TOKEN,
      tokens.refreshToken
    );
  }

  setUser(token: string): void {
    const payloadUser = this.jwtHelper.decodeToken(token);
    let user = payloadUser;
    this._obsUser.next(user);
    this.utilService.setSessionStorage(
      environment.VAR_USER,
      JSON.stringify(user)
    );
  }

  getUser(): UserAuth {
    return this.getStoredUser() ?? ({} as UserAuth);
  }

  getObsUser(): Observable<UserAuth> {
    return this._obsUser.asObservable();
  }

  public isAuthenticated(): boolean {
    const token = this.utilService.getSessionStorage(environment.VAR_TOKEN);
    return !this.jwtHelper.isTokenExpired(token);
  }

  existsRole(): boolean {
    const token = this.getToken();
    let roles: string[] = [];
    if (token && token !== '') {
      const tokenPayload = this.jwtHelper.decodeToken(token);
      const userInfoJSON = JSON.parse(tokenPayload.userInfo);
      const user = Object.assign({} as UserAuth, userInfoJSON);
      roles = user.roles;
    }
    return roles.length === 0;
  }

  logout(): Observable<void> {
    const refreshToken = this.getRefreshToken();
    this.clearLocalStorage();
    if (!refreshToken) {
      return of(void 0);
    }
    return this.http
      .post<void>(
        `${this.urlServiceAdmin}/auth/logout`,
        { refreshToken },
        { withCredentials: true }
      )
      .pipe(
        map(() => void 0),
        catchError(() => {
          this.utilService.alertWarnning(
            'La sesión local se cerró, pero no se pudo revocar la sesión remota.'
          );
          return of(void 0);
        })
      );
  }

  clearLocalStorage(): void {
    this.utilService.removeSessionStorage(environment.VAR_TOKEN);
    this.utilService.removeSessionStorage(environment.VAR_REFRESH_TOKEN);
    this.utilService.removeSessionStorage(environment.VAR_USER);
  }

  private getStoredUser(): UserAuth | null {
    const user = this.utilService.getSessionStorage(environment.VAR_USER);
    return user ? JSON.parse(user) : null;
  }

  postAuthenticationUser(request: any) {
    return this.http.post<any>(`${this.urlAuth}`, request, {
      withCredentials: true,
    });
  }

  postCreateUserAlertEarly(request: any) {
    return this.http.post<any>(`${this.url}/alert-user/early-alert`, request);
  }

  postCreateUserAlertEarlyGoogle(request: any) {
    return this.http.post<any>(
      `${this.url}/alert-user/early-alert-google`,
      request
    );
  }

  postLoginGoogle(request: any) {
    return this.http.post<any>(`${this.urlAuth}-google`, request, {
      withCredentials: true,
    });
  }

  changePassword(request: ChangePasswordRequest) {
    return this.http.post<void>(
      `${this.urlServiceAdmin}/api/protected/account/password`,
      request
    );
  }

  getPasswordStatus() {
    return this.http.get<{ passwordConfigured: boolean }>(
      `${this.urlServiceAdmin}/api/protected/account/password/status`
    );
  }
}
